import React, { useState, useEffect, useRef } from 'react';
import AdminService from '../services/adminService';
import '../css/admin/BSM.css';
import {
  FaRegBuilding,
  FaBuilding,
  FaEye,
  FaEdit,
  FaCheckCircle,
  FaTimesCircle,
  FaMoneyBillWave,
  FaRupeeSign,
  FaTrash,
  FaSpinner,
  FaSearch,
  FaFilter,
  FaTimes,
  FaSave,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaExclamationTriangle,
  FaChartLine,
  FaBox,
  FaUsers,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaTag,
  FaCrown,
  FaStore,
  FaPlus,
  FaDownload
} from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
import { Card, Row, Col, Statistic, Table, Tag, Progress, Avatar, Space, Typography, Button, Input, Select, Tooltip } from 'antd';
import BusinessStaffManagement from './BusinessStaffManagement';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const BusinessManagement = () => {
  const [businesses, setBusinesses] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    business_type: '',
    search: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [autoFilledAddress, setAutoFilledAddress] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [businessDetails, setBusinessDetails] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'orders'
  const [searchInput, setSearchInput] = useState(''); // Local search input state
  const searchTimeoutRef = useRef(null); // Ref for debounce timeout
  const [editModalLoading, setEditModalLoading] = useState(false); // Loading state for edit modal
  const [editableBusinessHours, setEditableBusinessHours] = useState({}); // State for editable business hours
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = screenSize.width <= 768;
  const isTablet = screenSize.width > 768 && screenSize.width <= 1024;
  const isDesktop = screenSize.width > 1024 && !(screenSize.width <= 1200 && screenSize.height <= 945);
  const isCompactDesktop = screenSize.width <= 1200 && screenSize.height <= 945 && screenSize.width > 1024;

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  const fetchBusinessTypes = async () => {
    try {
      const response = await AdminService.getBusinessTypes();
      let types = [];
      if (Array.isArray(response)) {
        types = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        types = response.data;
      } else if (response && response.businessTypes && Array.isArray(response.businessTypes)) {
        types = response.businessTypes;
      } else if (response) {
        for (const key in response) {
          if (Array.isArray(response[key])) {
            types = response[key];
            break;
          }
        }
      }
      setBusinessTypes(types);
    } catch (err) {
      console.error('Failed to fetch business types:', err);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, [filters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.custom-dropdown')) {
        setDropdownOpen(false);
      }
      if (statusDropdownOpen && !event.target.closest('.status-dropdown')) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, statusDropdownOpen]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data from the new businesses-simple API
      const response = await AdminService.getBusinessesSimple(filters);

      setBusinesses(response.businesses);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to fetch businesses');
      console.error('Businesses fetch error:', err);

      // Fallback to mock data if API fails


      setBusinesses(mockResponse.businesses);
      setPagination(mockResponse.pagination);
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed business information for edit modal
  const fetchBusinessDetails = async (businessId) => {
    try {
      const response = await AdminService.getBusinessDetailedView(businessId);
      if (response && response.business_info) {
        return response.business_info;
      } else if (response && response.data && response.data.business_info) {
        return response.data.business_info;
      } else if (response) {
        // If response exists but structure is different, return the response itself
        return response;
      }
      return null;
    } catch (err) {
      console.error('Failed to fetch business details:', err);
      return null;
    }
  };

  // Add new time slot for a specific day
  const addTimeSlot = (day) => {
    setEditableBusinessHours(prev => ({
      ...prev,
      [day]: [...(prev[day] || []), { open: '09:00', close: '17:00' }]
    }));
  };

  // Remove time slot for a specific day
  const removeTimeSlot = (day, index) => {
    setEditableBusinessHours(prev => {
      const currentSlots = prev[day] || [];
      if (currentSlots.length <= 1) {
        // Don't remove the last slot, just clear it
        return {
          ...prev,
          [day]: [{ open: '', close: '' }]
        };
      }
      return {
        ...prev,
        [day]: currentSlots.filter((_, i) => i !== index)
      };
    });
  };

  // Update time slot value
  const updateTimeSlot = (day, index, field, value) => {
    setEditableBusinessHours(prev => {
      const currentSlots = prev[day] || [];
      const updatedSlots = [...currentSlots];
      updatedSlots[index] = {
        ...updatedSlots[index],
        [field]: value
      };
      return {
        ...prev,
        [day]: updatedSlots
      };
    });
  };

  // Save business details
  const saveBusinessDetails = async () => {
    try {

      // Use current state values directly
      const currentData = {
        business_id: selectedBusiness.business_id,
        businessName: businessDetails.businessName || selectedBusiness.businessName || selectedBusiness.name,
        email: businessDetails.businessEmail || selectedBusiness.businessEmail,
        contact_mobile: businessDetails.businessNumber || selectedBusiness.businessNumber,
        city: businessDetails.city || selectedBusiness.city,
        state: businessDetails.state || selectedBusiness.state,
        pincode: businessDetails.pincode || selectedBusiness.pincode,
        address: businessDetails.address || selectedBusiness.address,
        status: (businessDetails.status !== undefined ? businessDetails.status : selectedBusiness.status) === 1 || (businessDetails.status !== undefined ? businessDetails.status : selectedBusiness.status) === 'Active' ? true : false,
        level: businessDetails.level || selectedBusiness.level,
        business_hours: Object.keys(editableBusinessHours).length > 0 ? editableBusinessHours : businessDetails.business_hours,
        description: businessDetails.description !== undefined ? businessDetails.description : selectedBusiness.description,
        logo: businessDetails.logo !== undefined ? businessDetails.logo : selectedBusiness.logo,
        banner: businessDetails.banner !== undefined ? businessDetails.banner : selectedBusiness.banner,
        fssai_number: businessDetails.fssai_number !== undefined ? businessDetails.fssai_number : selectedBusiness.fssai_number,
        gstin: businessDetails.gstin !== undefined ? businessDetails.gstin : selectedBusiness.gstin,
        owner_pan: businessDetails.owner_pan !== undefined ? businessDetails.owner_pan : selectedBusiness.owner_pan,
        account_number: businessDetails.account_number !== undefined ? businessDetails.account_number : selectedBusiness.account_number,
        ifsc_code: businessDetails.ifsc_code !== undefined ? businessDetails.ifsc_code : selectedBusiness.ifsc_code,
        owner_first_name: businessDetails.owner_first_name !== undefined ? businessDetails.owner_first_name : selectedBusiness.owner_first_name,
        owner_last_name: businessDetails.owner_last_name !== undefined ? businessDetails.owner_last_name : selectedBusiness.owner_last_name,
        owner_name: businessDetails.owner_name !== undefined ? businessDetails.owner_name : selectedBusiness.owner_name,
        owner_email: businessDetails.owner_email !== undefined ? businessDetails.owner_email : selectedBusiness.owner_email,
        owner_mobile: businessDetails.owner_mobile !== undefined ? businessDetails.owner_mobile : selectedBusiness.owner_mobile,
        latitude: businessDetails.latitude !== undefined ? businessDetails.latitude : selectedBusiness.latitude,
        longitude: businessDetails.longitude !== undefined ? businessDetails.longitude : selectedBusiness.longitude,
        is_verified: businessDetails.is_verified !== undefined ? businessDetails.is_verified : selectedBusiness.is_verified,
        payment_status: businessDetails.payment_status !== undefined ? businessDetails.payment_status : selectedBusiness.payment_status,
        razor_pay_key_id: businessDetails.razor_pay_key_id !== undefined ? businessDetails.razor_pay_key_id : selectedBusiness.razor_pay_key_id,
        razor_pay_key_code: businessDetails.razor_pay_key_code !== undefined ? businessDetails.razor_pay_key_code : selectedBusiness.razor_pay_key_code,
        razor_webhook_secret: businessDetails.razor_webhook_secret !== undefined ? businessDetails.razor_webhook_secret : selectedBusiness.razor_webhook_secret,
        payment_gateway: businessDetails.payment_gateway !== undefined ? businessDetails.payment_gateway : selectedBusiness.payment_gateway,
        gateway_credentials: businessDetails.gateway_credentials !== undefined ? businessDetails.gateway_credentials : selectedBusiness.gateway_credentials
      };

      // Get original data for comparison
      const originalData = {
        businessName: selectedBusiness.businessName || selectedBusiness.name,
        email: selectedBusiness.businessEmail,
        contact_mobile: selectedBusiness.businessNumber,
        city: selectedBusiness.city,
        state: selectedBusiness.state,
        pincode: selectedBusiness.pincode,
        address: selectedBusiness.address,
        status: selectedBusiness.status === 1 || selectedBusiness.status === 'Active' ? true : false,
        level: selectedBusiness.level,
        business_hours: selectedBusiness.business_hours,
        description: selectedBusiness.description,
        logo: selectedBusiness.logo,
        banner: selectedBusiness.banner,
        fssai_number: selectedBusiness.fssai_number,
        gstin: selectedBusiness.gstin,
        owner_pan: selectedBusiness.owner_pan,
        account_number: selectedBusiness.account_number,
        ifsc_code: selectedBusiness.ifsc_code,
        owner_first_name: selectedBusiness.owner_first_name,
        owner_last_name: selectedBusiness.owner_last_name,
        owner_name: selectedBusiness.owner_name,
        owner_email: selectedBusiness.owner_email,
        owner_mobile: selectedBusiness.owner_mobile,
        latitude: selectedBusiness.latitude,
        longitude: selectedBusiness.longitude,
        is_verified: selectedBusiness.is_verified,
        payment_status: selectedBusiness.payment_status,
        razor_pay_key_id: selectedBusiness.razor_pay_key_id,
        razor_pay_key_code: selectedBusiness.razor_pay_key_code,
        razor_webhook_secret: selectedBusiness.razor_webhook_secret,
        payment_gateway: selectedBusiness.payment_gateway,
        gateway_credentials: selectedBusiness.gateway_credentials
      };

      // Compare and find only changed fields
      const changedFields = {};

      // Check business hours separately
      if (Object.keys(editableBusinessHours).length > 0) {
        const currentBusinessHours = editableBusinessHours;
        let parsedOriginalHours = originalData.business_hours;

        // Parse original business hours if it's a string for proper comparison
        if (typeof parsedOriginalHours === 'string') {
          try {
            parsedOriginalHours = JSON.parse(parsedOriginalHours);
          } catch (error) {
            console.error('Error parsing original business hours:', error);
            parsedOriginalHours = {};
          }
        }

        // Compare JSON objects properly
        if (JSON.stringify(currentBusinessHours) !== JSON.stringify(parsedOriginalHours)) {
          changedFields.business_hours = currentBusinessHours;
        }
      }

      // Check gateway_credentials separately
      let parsedCurrentCreds = currentData.gateway_credentials;
      let parsedOriginalCreds = originalData.gateway_credentials;

      if (typeof parsedCurrentCreds === 'string' && parsedCurrentCreds.trim() !== '') {
        try { parsedCurrentCreds = JSON.parse(parsedCurrentCreds); } catch (e) { console.error('Error parsing gateway credentials:', e); }
      }
      if (typeof parsedOriginalCreds === 'string' && parsedOriginalCreds.trim() !== '') {
        try { parsedOriginalCreds = JSON.parse(parsedOriginalCreds); } catch (e) { }
      }

      if (JSON.stringify(parsedCurrentCreds) !== JSON.stringify(parsedOriginalCreds)) {
        if (parsedCurrentCreds !== undefined) {
          changedFields.gateway_credentials = parsedCurrentCreds;
        }
      }

      // Check other fields
      Object.keys(currentData).forEach(key => {
        if (key === 'business_id' || key === 'business_hours' || key === 'gateway_credentials') return; // Skip handled separately fields

        const currentValue = currentData[key];
        const originalValue = originalData[key];

        // For other fields, check if value has changed
        if (currentValue !== originalValue) {
          changedFields[key] = currentValue;
        }
      });

      // Always include business_id
      changedFields.business_id = selectedBusiness.business_id;

      // Check if any fields were actually changed (excluding business_id)
      const actualChangedFields = { ...changedFields };
      delete actualChangedFields.business_id;

      if (Object.keys(actualChangedFields).length === 0 && !businessDetails.logo_file && !businessDetails.banner_file) {
        return;
      }

      let payload = changedFields;
      if (businessDetails.logo_file || businessDetails.banner_file) {
        payload = new FormData();
        Object.keys(changedFields).forEach(key => {
          const value = changedFields[key];
          if (typeof value === 'object' && value !== null) {
            payload.append(key, JSON.stringify(value));
          } else {
            payload.append(key, value);
          }
        });
        if (businessDetails.logo_file) payload.append('logo', businessDetails.logo_file);
        if (businessDetails.banner_file) payload.append('banner', businessDetails.banner_file);
      }

      // Call the update API with only changed fields
      const response = await AdminService.updateBusinessDetails(payload);

      if (response && response.success) {
        setShowEditModal(false);
        // Reset business details state
        setBusinessDetails({});
        setEditableBusinessHours({});
        // Refresh business list
        fetchBusinesses();
      } else {
        console.error('Failed to update business details');
      }
    } catch (error) {
      console.error('Error saving business details:', error);
    }
  };

  // Calculate summary statistics
  const calculateStats = () => {
    const totalBusinesses = businesses.length;
    const activeBusinesses = businesses.filter(b => b.status === 'Active').length;
    const totalRevenue = businesses.reduce((sum, b) => sum + (b.total_revenue || 0), 0);
    const totalOrders = businesses.reduce((sum, b) => sum + (b.total_orders || 0), 0);
    const masterBusinesses = businesses.filter(b => b.is_master).length;
    const subBusinesses = businesses.filter(b => !b.is_master).length;

    return {
      totalBusinesses,
      activeBusinesses,
      totalRevenue,
      totalOrders,
      masterBusinesses,
      subBusinesses
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'green',
      'Inactive': 'red',
      'Pending': 'orange',
      'Paid': 'blue',
      'Unpaid': 'red'
    };
    return colors[status] || 'default';
  };

  const getLevelIcon = (business) => {
    if (business.is_master) {
      return <FaCrown style={{ color: '#FFD700' }} />;
    }
    return <FaRegBuilding style={{ color: '#1890ff' }} />;
  };

  const formatCurrency = (amount) => {
    const num = amount || 0;

    if (num >= 10000000) { // 1 Crore or more
      return `₹${(num / 10000000).toFixed(2)} Cr`;
    } else if (num >= 100000) { // 1 Lakh or more
      return `₹${(num / 100000).toFixed(2)} Lac`;
    } else if (num >= 1000) { // 1 Thousand or more
      return `₹${(num / 1000).toFixed(2)} K`;
    } else {
      return `₹${num.toFixed(2)}`;
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value || '', // Convert undefined/null to empty string
        page: 1 // Reset to first page when filters change
      };
      return newFilters;
    });
  };

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout to update filters after 500ms of no typing
    searchTimeoutRef.current = setTimeout(() => {
      handleFilterChange('search', value);
    }, 500);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleViewDetails = (business) => {
    // Navigate to the new tabbed business details page
    window.location.hash = `business-details-tabbed/${business.business_id}`;
  };

  const handleEditBusiness = async (business) => {
    // Open edit modal and fetch detailed business information
    setSelectedBusiness(business);
    setEditModalLoading(true);
    setShowEditModal(true);

    try {
      // Fetch detailed business information from API
      const detailedInfo = await fetchBusinessDetails(business.business_id);
      if (detailedInfo) {
        setBusinessDetails(detailedInfo);

        // Initialize editable business hours
        if (detailedInfo.business_hours) {
          try {
            const parsedHours = typeof detailedInfo.business_hours === 'string'
              ? JSON.parse(detailedInfo.business_hours)
              : detailedInfo.business_hours;
            setEditableBusinessHours(parsedHours);
          } catch (error) {
            console.error('Error parsing business hours for editing:', error);
            setEditableBusinessHours({});
          }
        }

        // Initialize auto-filled address with detailed info if available
        if (detailedInfo.city && detailedInfo.state) {
          generateAddress(detailedInfo.city, detailedInfo.state);
        }
      } else {
        // Fallback to basic business info if API fails
        if (business.city && business.state) {
          generateAddress(business.city, business.state);
        }
      }
    } catch (error) {
      console.error('Error fetching business details:', error);
      // Fallback to basic business info
      if (business.city && business.state) {
        generateAddress(business.city, business.state);
      }
    } finally {
      setEditModalLoading(false);
    }
  };

  const generateAddress = (city, state) => {
    // Generate address based on city and state
    if (city && state) {
      const cityLower = city.toLowerCase();
      let generatedAddress = '';

      // Address generation logic based on city
      if (cityLower.includes('sri city') || cityLower.includes('sri')) {
        generatedAddress = '120 East Jeedi Drive';
      } else if (cityLower.includes('hyderabad') || cityLower.includes('hyd')) {
        generatedAddress = '100 MG Road, Banjara Hills';
      } else if (cityLower.includes('bangalore') || cityLower.includes('bengaluru')) {
        generatedAddress = '50 Brigade Road, MG Road';
      } else if (cityLower.includes('chennai') || cityLower.includes('madras')) {
        generatedAddress = '200 Anna Salai, T Nagar';
      } else if (cityLower.includes('mumbai') || cityLower.includes('bombay')) {
        generatedAddress = '75 Linking Road, Bandra';
      } else if (cityLower.includes('delhi') || cityLower.includes('new delhi')) {
        generatedAddress = '15 Connaught Place, Janpath';
      } else if (cityLower.includes('kolkata') || cityLower.includes('calcutta')) {
        generatedAddress = '25 Park Street, Esplanade';
      } else {
        // Default address generation
        generatedAddress = `${Math.floor(Math.random() * 999) + 1} ${city} Main Road`;
      }

      setAutoFilledAddress(generatedAddress);
    } else {
      setAutoFilledAddress('');
    }
  };

  const handleCityChange = (city) => {
    const state = selectedBusiness?.state || '';
    generateAddress(city, state);
  };

  const handleStateChange = (state) => {
    const city = selectedBusiness?.city || '';
    generateAddress(city, state);
  };

  const stats = calculateStats();

  // Table columns for business data
  const columns = [
    {
      title: 'Business Details',
      key: 'businessDetails',
      width: '35%',
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {/* Left side: Business name and ID */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 16, color: '#1890ff', flexShrink: 0 }}>
              {getLevelIcon(record)}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontWeight: 600,
                fontSize: 15,
                color: '#2A2C41',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginBottom: 3,
                lineHeight: 1.3
              }}>
                {record.businessName}
              </div>
              <Text type="secondary" style={{
                fontSize: 12,
                fontFamily: 'monospace',
                color: '#8c8c8c',
                fontWeight: 500
              }}>
                {record.business_id}
              </Text>
            </div>
          </div>

          {/* Right side: Business Type and Category tags */}
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
            <Tag
              color="geekblue"
              style={{
                borderRadius: 4,
                fontWeight: 500,
                fontSize: 10,
                padding: '2px 8px'
              }}
            >
              {record.businessType}
            </Tag>
            <Tag
              color="blue"
              style={{
                borderRadius: 4,
                fontWeight: 500,
                fontSize: 10,
                padding: '1px 6px'
              }}
            >
              {record.businessCategory}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Location',
      key: 'location',
      width: '15%',
      render: (_, record) => (
        <div style={{ lineHeight: 1.3 }}>
          <div style={{
            fontSize: 12,
            fontWeight: 500,
            color: '#2A2C41',
            marginBottom: 1
          }}>
            {record.city}, {record.state}
          </div>
          <Text type="secondary" style={{
            fontSize: 10,
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '140px'
          }}>
            {record.address}
          </Text>
        </div>
      )
    },
    {
      title: 'Registration Date',
      key: 'registrationDate',
      width: '12%',
      align: 'center',
      render: (_, record) => {
        const date = new Date(record.created_at);
        const formattedDate = date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
        const timeAgo = (() => {
          const now = new Date();
          const diffTime = Math.abs(now - date);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays < 30) return `${diffDays}d ago`;
          if (diffDays < 365) return `${Math.floor(diffDays / 30)}m ago`;
          return `${Math.floor(diffDays / 365)}y ago`;
        })();

        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: 11,
              fontWeight: 500,
              color: '#2A2C41',
              marginBottom: 1
            }}>
              {formattedDate}
            </div>
            <Text type="secondary" style={{ fontSize: 9 }}>
              {timeAgo}
            </Text>
          </div>
        );
      }
    },
    {
      title: 'Performance',
      key: 'performance',
      width: '15%',
      align: 'center',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <Text strong style={{ fontSize: 13, color: '#2A2C41', display: 'block' }}>
              {record.total_orders || 0}
            </Text>
            <Text type="secondary" style={{ fontSize: 9 }}>
              orders
            </Text>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Text strong style={{ fontSize: 12, color: '#52c41a', display: 'block' }}>
              {formatCurrency(record.total_revenue)}
            </Text>
            <Text type="secondary" style={{ fontSize: 9 }}>
              revenue
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: '10%',
      align: 'center',
      render: (_, record) => {
        const isActive = record.status_code === 1 || record.status === 'Active';
        return (
          <Tag
            color={isActive ? 'success' : 'error'}
            style={{
              fontSize: 11,
              fontWeight: 500,
              borderRadius: 12,
              padding: '2px 8px',
              textTransform: 'capitalize'
            }}
          >
            {isActive ? 'Active' : 'Inactive'}
          </Tag>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '16%',
      align: 'center',
      render: (_, record) => (
        <Space size={6}>
          <Button
            type="primary"
            size="small"
            onClick={() => handleViewDetails(record)}
            style={{
              fontSize: 10,
              height: 28,
              padding: '0 8px',
              borderRadius: 4
            }}
          >
            View
          </Button>
          <Button
            type="default"
            size="small"
            onClick={() => handleEditBusiness(record)}
            style={{
              fontSize: 10,
              height: 28,
              padding: '0 8px',
              borderRadius: 4,
              backgroundColor: '#f0f0f0',
              borderColor: '#d9d9d9',
              color: '#595959'
            }}
          >
            Edit
          </Button>

        </Space>
      )
    }
  ];



  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 16
      }}>
        <FaSpinner className="loading-spinner" style={{ fontSize: 48, color: '#1890ff' }} />
        <p style={{ color: '#666' }}>Loading businesses...</p>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '8px' : isTablet ? '12px' : '20px',
      background: '#f5f5f5',
      minHeight: '100vh',
    }}>
      {/* Error Display */}
      {error && (
        <div className="bsm-error-message">
          <FaExclamationTriangle />
          <span className="bsm-error-text">{error}</span>
          <button
            className="bsm-error-close"
            onClick={() => setError(null)}
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Business Management Card */}
      <div className="bsm-business-management-card">
        <div className="bsm-business-management-header">
          <div className="bsm-business-management-title">
            <FaStore className="bsm-business-management-icon" style={{ color: 'black' }} />
            <h3 style={{ color: 'black', margin: 0 }}>Business Management</h3>
          </div>
        </div>
      </div>

      {/* Statistics Cards - Fixed 6 Column Layout */}
      <div className={`bsm-stats-grid ${isMobile ? 'mobile' : isCompactDesktop ? 'compact-desktop' : isTablet ? 'tablet' : 'desktop'}`}>
        {[
          {
            title: "Total Businesses",
            value: stats.totalBusinesses,
            icon: <FaBuilding />,
            color: "#1890ff"
          },
          {
            title: "Active Businesses",
            value: stats.activeBusinesses,
            icon: <FaCheckCircle />,
            color: "#52c41a"
          },
          {
            title: "Total Revenue",
            value: formatCurrency(stats.totalRevenue),
            icon: <FaRupeeSign />,
            color: "#faad14"
          },
          {
            title: "Total Orders",
            value: stats.totalOrders,
            icon: <FaBox />,
            color: "#722ed1"
          },
          {
            title: "Master Businesses",
            value: stats.masterBusinesses,
            icon: <FaCrown />,
            color: "#eb2f96"
          },
          {
            title: "Sub-Businesses",
            value: stats.subBusinesses,
            icon: <FaStore />,
            color: "#13c2c2"
          }
        ].map((stat, index) => {
          const colorClass = stat.color === '#1890ff' ? 'blue' :
            stat.color === '#52c41a' ? 'green' :
              stat.color === '#faad14' ? 'orange' :
                stat.color === '#722ed1' ? 'purple' :
                  stat.color === '#eb2f96' ? 'pink' : 'cyan';

          return (
            <div
              key={index}
              className={`bsm-stat-card ${colorClass} ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}
            >
              <div className={`bsm-stat-header ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>
                <div className={`bsm-stat-icon ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>
                  {stat.icon}
                </div>
                <div className={`bsm-stat-title ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>
                  {stat.title}
                </div>
              </div>
              <div className={`bsm-stat-value ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>
                {stat.value}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters and Search - Responsive HTML */}
      <div style={{
        marginBottom: isMobile ? '12px' : isTablet ? '16px' : '24px',
        padding: isMobile ? '12px' : isTablet ? '16px' : '16px',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '12px' : '16px',
          alignItems: 'center',
          justifyContent: isMobile ? 'stretch' : 'flex-start'
        }}>
          {/* Search Input */}
          <div style={{
            width: isMobile ? '100%' : '200px',
            flexShrink: 0
          }}>
            <input
              type="text"
              placeholder="Search businesses..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                handleFilterChange('search', e.target.value);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleFilterChange('search', e.target.value);
                }
              }}
              style={{
                width: '100%',
                padding: isMobile ? '8px 12px' : '10px 14px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                fontSize: isMobile ? '14px' : '16px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1890ff';
                e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d9d9d9';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Status Filter */}
          <div style={{
            width: isMobile ? '100%' : '200px',
            flexShrink: 0
          }}>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{
                width: '100%',
                padding: isMobile ? '8px 12px' : '10px 14px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                fontSize: isMobile ? '14px' : '16px',
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1890ff';
                e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d9d9d9';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Pending">Pending</option>
            </select>
          </div>

          {/* Category Filter */}
          <div style={{
            width: isMobile ? '100%' : '200px',
            flexShrink: 0
          }}>
            <select
              value={filters.business_type || ''}
              onChange={(e) => handleFilterChange('business_type', e.target.value)}
              style={{
                width: '100%',
                padding: isMobile ? '8px 12px' : '10px 14px',
                borderRadius: '6px',
                fontSize: isMobile ? '14px' : '16px',
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1890ff';
                e.target.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d9d9d9';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">All Categories</option>
              {businessTypes.map((type, index) => {
                const value = type.business_type_id || type.code || type.id || type.name;
                const label = type.title || type.type || type.business_type_name || type.name || type.type_name || value;
                return (
                  <option key={index} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {/* Businesses Table - Custom HTML/React */}
      <div className="bsm-table-container">

        {/* Table Container with Internal Scrolling */}
        <div className="bsm-table-wrapper">
          <table>
            {/* Table Headers */}
            <thead>
              <tr style={{
                background: '#fafafa',
                borderBottom: '2px solid #f0f0f0'
              }}>
                <th style={{
                  padding: isMobile ? '12px 8px' : '14px 12px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#2A2C41',
                  fontSize: isMobile ? '12px' : '13px',
                  borderBottom: '1px solid #e8e8e8'
                }}>
                  Business Details
                </th>
                <th style={{
                  padding: isMobile ? '12px 8px' : '14px 12px',
                  textAlign: 'center',
                  fontWeight: 600,
                  color: '#2A2C41',
                  fontSize: isMobile ? '12px' : '13px',
                  borderBottom: '1px solid #e8e8e8'
                }}>
                  Business Type
                </th>
                <th style={{
                  padding: isMobile ? '12px 8px' : '14px 12px',
                  textAlign: 'center',
                  fontWeight: 600,
                  color: '#2A2C41',
                  fontSize: isMobile ? '12px' : '13px',
                  borderBottom: '1px solid #e8e8e8'
                }}>
                  Business Category
                </th>
                <th style={{
                  padding: isMobile ? '12px 8px' : '14px 12px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#2A2C41',
                  fontSize: isMobile ? '12px' : '13px',
                  borderBottom: '1px solid #e8e8e8'
                }}>
                  Location
                </th>
                <th style={{
                  padding: isMobile ? '12px 8px' : '14px 12px',
                  textAlign: 'center',
                  fontWeight: 600,
                  color: '#2A2C41',
                  fontSize: isMobile ? '12px' : '13px',
                  borderBottom: '1px solid #e8e8e8'
                }}>
                  Registration Date
                </th>
                <th style={{
                  padding: isMobile ? '12px 8px' : '14px 12px',
                  textAlign: 'center',
                  fontWeight: 600,
                  color: '#2A2C41',
                  fontSize: isMobile ? '12px' : '13px',
                  borderBottom: '1px solid #e8e8e8'
                }}>
                  Performance
                </th>
                <th style={{
                  padding: isMobile ? '12px 8px' : '14px 12px',
                  textAlign: 'center',
                  fontWeight: 600,
                  color: '#2A2C41',
                  fontSize: isMobile ? '12px' : '13px',
                  borderBottom: '1px solid #e8e8e8'
                }}>
                  Status
                </th>
                <th style={{
                  padding: isMobile ? '12px 8px' : '14px 12px',
                  textAlign: 'center',
                  fontWeight: 600,
                  color: '#2A2C41',
                  fontSize: isMobile ? '12px' : '13px',
                  borderBottom: '1px solid #e8e8e8'
                }}>
                  Actions
                </th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{
                    padding: isMobile ? '40px 16px' : '60px 20px',
                    textAlign: 'center',
                    color: '#8c8c8c'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '16px'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        border: '3px solid #f0f0f0',
                        borderTop: '3px solid #1890ff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <div>Loading businesses...</div>
                    </div>
                  </td>
                </tr>
              ) : businesses.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{
                    padding: isMobile ? '40px 16px' : '60px 20px',
                    textAlign: 'center',
                    color: '#8c8c8c'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '16px'
                    }}>
                      <div style={{
                        fontSize: isMobile ? '24px' : '32px',
                        color: '#d9d9d9'
                      }}>📊</div>
                      <div>No businesses found</div>
                    </div>
                  </td>
                </tr>
              ) : (
                businesses
                  .slice((currentPage - 1) * 10, currentPage * 10)
                  .map((business, index) => (
                    <tr key={index} style={{
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background-color 0.2s ease'
                    }}>
                      {/* Business Details Column */}
                      <td data-label="Business Details" style={{
                        padding: isMobile ? '12px 8px' : '14px 12px',
                        verticalAlign: 'middle'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                          <span style={{ fontSize: isMobile ? '14px' : '16px', color: '#1890ff', flexShrink: 0 }}>
                            {getLevelIcon(business)}
                          </span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{
                              fontWeight: 600,
                              fontSize: isMobile ? '13px' : '15px',
                              color: '#2A2C41',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              marginBottom: 3,
                              lineHeight: 1.3
                            }}>
                              {business.businessName}
                            </div>
                            <div style={{
                              fontSize: isMobile ? '11px' : '12px',
                              fontFamily: 'monospace',
                              color: '#8c8c8c',
                              fontWeight: 500
                            }}>
                              {business.business_id}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Business Type Column */}
                      <td data-label="Business Type" style={{
                        padding: isMobile ? '12px 8px' : '14px 12px',
                        verticalAlign: 'middle',
                        textAlign: 'center'
                      }}>
                        <span style={{
                          backgroundColor: '#e6f4ff',
                          color: '#0958d9',
                          borderRadius: 4,
                          fontWeight: 500,
                          fontSize: isMobile ? '9px' : '10px',
                          padding: '2px 8px',
                          border: '1px solid #91caff'
                        }}>
                          {business.businessType}
                        </span>
                      </td>

                      {/* Business Category Column */}
                      <td data-label="Category" style={{
                        padding: isMobile ? '12px 8px' : '14px 12px',
                        verticalAlign: 'middle',
                        textAlign: 'center'
                      }}>
                        <span style={{
                          backgroundColor: '#f0f5ff',
                          color: '#002766',
                          borderRadius: 4,
                          fontWeight: 500,
                          fontSize: isMobile ? '9px' : '10px',
                          padding: '1px 6px',
                          border: '1px solid #adc6ff'
                        }}>
                          {business.businessCategory}
                        </span>
                      </td>

                      {/* Location Column */}
                      <td data-label="Location" style={{
                        padding: isMobile ? '12px 8px' : '14px 12px',
                        verticalAlign: 'middle'
                      }}>
                        <div style={{ lineHeight: 1.3 }}>
                          <div style={{
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: 500,
                            color: '#2A2C41',
                            marginBottom: 1
                          }}>
                            {business.city}, {business.state}
                          </div>
                          <div style={{
                            fontSize: isMobile ? '9px' : '10px',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '240px',
                            color: '#8c8c8c'
                          }}>
                            {business.address}
                          </div>
                        </div>
                      </td>

                      {/* Registration Date Column */}
                      <td data-label="Registration Date" style={{
                        padding: isMobile ? '12px 8px' : '14px 12px',
                        verticalAlign: 'middle',
                        textAlign: 'center'
                      }}>
                        {(() => {
                          const date = new Date(business.created_at);
                          const formattedDate = date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          });
                          const timeAgo = (() => {
                            const now = new Date();
                            const diffTime = Math.abs(now - date);
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            if (diffDays < 30) return `${diffDays}d ago`;
                            if (diffDays < 365) return `${Math.floor(diffDays / 30)}m ago`;
                            return `${Math.floor(diffDays / 365)}y ago`;
                          })();

                          return (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{
                                fontSize: isMobile ? '10px' : '11px',
                                fontWeight: 500,
                                color: '#2A2C41',
                                marginBottom: 1
                              }}>
                                {formattedDate}
                              </div>
                              <div style={{ fontSize: isMobile ? '8px' : '9px', color: '#8c8c8c' }}>
                                {timeAgo}
                              </div>
                            </div>
                          );
                        })()}
                      </td>

                      {/* Performance Column */}
                      <td data-label="Performance" style={{
                        padding: isMobile ? '12px 8px' : '14px 12px',
                        verticalAlign: 'middle',
                        textAlign: 'center'
                      }}>
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#2A2C41', display: 'block', fontWeight: 600 }}>
                              {business.total_orders || 0}
                            </div>
                            <div style={{ fontSize: isMobile ? '8px' : '9px', color: '#8c8c8c' }}>
                              orders
                            </div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#52c41a', display: 'block', fontWeight: 600 }}>
                              {formatCurrency(business.total_revenue)}
                            </div>
                            <div style={{ fontSize: isMobile ? '8px' : '9px', color: '#8c8c8c' }}>
                              revenue
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Status Column */}
                      <td data-label="Status" style={{
                        padding: isMobile ? '12px 8px' : '14px 12px',
                        verticalAlign: 'middle',
                        textAlign: 'center'
                      }}>
                        <Tag
                          color={business.status_code === 1 || business.status === 'Active' ? 'success' : 'error'}
                          style={{
                            fontSize: isMobile ? 10 : 11,
                            fontWeight: 500,
                            borderRadius: 12,
                            padding: '2px 8px',
                            textTransform: 'capitalize'
                          }}
                        >
                          {business.status_code === 1 || business.status === 'Active' ? 'Active' : 'Inactive'}
                        </Tag>
                      </td>

                      {/* Actions Column */}
                      <td data-label="Actions" style={{
                        padding: isMobile ? '8px 4px' : '12px 8px',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        minWidth: isMobile ? '200px' : '250px'
                      }}>
                        <div style={{
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          maxWidth: '100%',
                          whiteSpace: 'nowrap'
                        }}>
                          <div style={{
                            display: 'inline-flex',
                            gap: 6,
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            padding: '2px 0'
                          }}>
                            <button
                              onClick={() => handleViewDetails(business)}
                              style={{
                                fontSize: isMobile ? '9px' : '10px',
                                height: isMobile ? '34px' : '28px',
                                padding: '0 8px',
                                borderRadius: 4,
                                border: 'none',
                                backgroundColor: '#1890ff',
                                color: 'white',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                flexShrink: 0
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#40a9ff'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#1890ff'}
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleEditBusiness(business)}
                              style={{
                                fontSize: isMobile ? '9px' : '10px',
                                height: isMobile ? '34px' : '28px',
                                padding: '0 8px',
                                borderRadius: 4,
                                border: '1px solid #d9d9d9',
                                backgroundColor: '#f0f0f0',
                                color: '#595959',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                flexShrink: 0
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#e6f7ff';
                                e.target.style.borderColor = '#1890ff';
                                e.target.style.color = '#1890ff';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#f0f0f0';
                                e.target.style.borderColor = '#d9d9d9';
                                e.target.style.color = '#595959';
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {businesses.length > 0 && (
          <div className="custom-pagination-container">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="pagination-btn-prev"
            >
              <FaChevronLeft />
            </button>
            <div className="pagination-page-info">
              <span className="current-page">{currentPage}</span>
              <span className="page-total">of {Math.ceil(businesses.length / 10)}</span>
            </div>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= Math.ceil(businesses.length / 10)}
              className="pagination-btn-next"
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* Action modal removed */}

      {/* Edit Business Modal */}
      {showEditModal && selectedBusiness && (
        <div className="edit-business-modal-overlay">
          <div className="edit-business-modal-container" style={{
            maxWidth: isMobile ? '95%' : '800px',
            width: isMobile ? '95%' : '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            fontSize: isMobile ? '14px' : '16px'
          }}>
            {/* Modal Header */}
            <div className="edit-business-modal-header" style={{ padding: isMobile ? '12px 16px' : '16px 20px' }}>
              <div className="edit-business-modal-title-section">
                <div className="edit-business-modal-icon" style={{ fontSize: isMobile ? '16px' : '20px' }}>
                  <FaEdit />
                </div>
                <h3 style={{ fontSize: isMobile ? '16px' : '18px', margin: 0 }}>Edit Business Details</h3>
              </div>
              <button
                className="edit-business-modal-close-btn"
                onClick={() => setShowEditModal(false)}
                style={{ fontSize: isMobile ? '14px' : '16px' }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Content */}
            <div className="edit-business-modal-content" style={{ padding: isMobile ? '12px 16px' : '20px 24px' }}>
              {editModalLoading ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: isMobile ? '40px' : '60px',
                  flexDirection: 'column',
                  gap: 16
                }}>
                  <FaSpinner className="loading-spinner" style={{ fontSize: isMobile ? '24px' : '32px', color: '#1890ff' }} />
                  <p style={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }}>Loading business details...</p>
                </div>
              ) : (
                <div className="edit-business-details-grid">
                  {/* Basic Information */}
                  <div className="edit-business-details-section">
                    <h4 style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '8px' : '12px' }}>Basic Information</h4>
                    <div className="edit-business-detail-item basic-info">
                      <div className="field-group">
                        <label>Business Name</label>
                        <input
                          type="text"
                          value={businessDetails.businessName || selectedBusiness.businessName || selectedBusiness.name}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, businessName: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Business ID</label>
                        <input
                          type="text"
                          value={selectedBusiness.business_id}
                          className="edit-business-input"
                          disabled
                        />
                      </div>
                      <div className="field-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={businessDetails.businessEmail || selectedBusiness.businessEmail}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, businessEmail: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          value={businessDetails.businessNumber || selectedBusiness.businessNumber}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, businessNumber: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Level</label>
                        <select
                          className="edit-business-input"
                          value={businessDetails.level || selectedBusiness.level || 'master'}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, level: e.target.value }))}
                        >
                          <option value="master">Master</option>
                          <option value="sublevel">Sublevel</option>
                        </select>
                      </div>
                      <div className="field-group full-width">
                        <label>Description</label>
                        <textarea
                          value={businessDetails.description || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, description: e.target.value }))}
                          className="edit-business-input"
                          rows="3"
                        />
                      </div>
                      <div className="field-group full-width" style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                        <div className="field-group" style={{ flex: 1 }}>
                          <label>Status</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', color: '#666' }}>
                              {(businessDetails.status !== undefined ? businessDetails.status : selectedBusiness.status) === 1 || (businessDetails.status !== undefined ? businessDetails.status : selectedBusiness.status) === 'Active' ? 'Active' : 'Inactive'}
                            </span>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={(businessDetails.status !== undefined ? businessDetails.status : selectedBusiness.status) === 1 || (businessDetails.status !== undefined ? businessDetails.status : selectedBusiness.status) === 'Active'}
                                onChange={(e) => {
                                  const newStatus = e.target.checked ? 'Active' : 'Inactive';
                                  setBusinessDetails(prev => ({ ...prev, status: newStatus }));
                                }}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                        </div>
                        <div className="field-group" style={{ flex: 1 }}>
                          <label>Is Verified</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', color: '#666' }}>
                              {(businessDetails.is_verified !== undefined ? businessDetails.is_verified : selectedBusiness.is_verified) === 1 ? 'Yes' : 'No'}
                            </span>
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={(businessDetails.is_verified !== undefined ? businessDetails.is_verified : selectedBusiness.is_verified) === 1}
                                onChange={(e) => setBusinessDetails(prev => ({ ...prev, is_verified: e.target.checked ? 1 : 0 }))}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                        </div>
                        <div className="field-group" style={{ flex: 1 }}>
                          <label>Payment Status</label>
                          <select
                            className="edit-business-input"
                            value={businessDetails.payment_status !== undefined ? businessDetails.payment_status : selectedBusiness.payment_status}
                            onChange={(e) => setBusinessDetails(prev => ({ ...prev, payment_status: Number(e.target.value) }))}
                          >
                            <option value={1}>Paid/Active</option>
                            <option value={0}>Unpaid/Inactive</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Media / Images */}
                  <div className="edit-business-details-section">
                    <h4 style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '8px' : '12px' }}>Media Information</h4>
                    <div className="edit-business-detail-item basic-info">
                      <div className="field-group full-width" style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                        <div className="field-group" style={{ flex: 1 }}>
                          <label>Logo</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                setBusinessDetails(prev => ({ ...prev, logo_file: e.target.files[0] }))
                              }
                            }}
                            className="edit-business-input"
                          />
                          {businessDetails.logo_file ? (
                            <div style={{ marginTop: '8px' }}>
                              <img src={URL.createObjectURL(businessDetails.logo_file)} alt="Logo Preview" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                            </div>
                          ) : businessDetails.logo && typeof businessDetails.logo === 'string' && (
                            <div style={{ marginTop: '8px' }}>
                              <img src={businessDetails.logo.startsWith('http') ? businessDetails.logo : `/${businessDetails.logo}`} alt="Logo" style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }} />
                            </div>
                          )}
                        </div>
                        <div className="field-group" style={{ flex: 1 }}>
                          <label>Banner</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                setBusinessDetails(prev => ({ ...prev, banner_file: e.target.files[0] }))
                              }
                            }}
                            className="edit-business-input"
                          />
                          {businessDetails.banner_file ? (
                            <div style={{ marginTop: '8px' }}>
                              <img src={URL.createObjectURL(businessDetails.banner_file)} alt="Banner Preview" style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain' }} />
                            </div>
                          ) : businessDetails.banner && typeof businessDetails.banner === 'string' && (
                            <div style={{ marginTop: '8px' }}>
                              <img src={businessDetails.banner.startsWith('http') ? businessDetails.banner : `/${businessDetails.banner}`} alt="Banner" style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain' }} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Business Documents */}
                  <div className="edit-business-details-section">
                    <h4 style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '8px' : '12px' }}>Business Details</h4>
                    <div className="edit-business-detail-item basic-info">
                      <div className="field-group">
                        <label>FSSAI Number</label>
                        <input
                          type="text"
                          value={businessDetails.fssai_number || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, fssai_number: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>GSTIN</label>
                        <input
                          type="text"
                          value={businessDetails.gstin || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, gstin: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Owner PAN</label>
                        <input
                          type="text"
                          value={businessDetails.owner_pan || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, owner_pan: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bank Details */}
                  <div className="edit-business-details-section">
                    <h4 style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '8px' : '12px' }}>Bank Details</h4>
                    <div className="edit-business-detail-item basic-info">
                      <div className="field-group">
                        <label>Account Number</label>
                        <input
                          type="text"
                          value={businessDetails.account_number || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, account_number: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>IFSC Code</label>
                        <input
                          type="text"
                          value={businessDetails.ifsc_code || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, ifsc_code: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Owner Information */}
                  <div className="edit-business-details-section">
                    <h4 style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '8px' : '12px' }}>Owner Information</h4>
                    <div className="edit-business-detail-item basic-info">
                      <div className="field-group">
                        <label>First Name</label>
                        <input
                          type="text"
                          value={businessDetails.owner_first_name || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, owner_first_name: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Last Name</label>
                        <input
                          type="text"
                          value={businessDetails.owner_last_name || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, owner_last_name: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Full Name</label>
                        <input
                          type="text"
                          value={businessDetails.owner_name || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, owner_name: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={businessDetails.owner_email || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, owner_email: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Mobile</label>
                        <input
                          type="text"
                          value={businessDetails.owner_mobile || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, owner_mobile: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="edit-business-details-section">
                    <h4 style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '8px' : '12px' }}>Address Information</h4>
                    <div className="edit-business-detail-item basic-info">
                      <div className="field-group">
                        <label>City</label>
                        <input
                          type="text"
                          value={businessDetails.city !== undefined ? businessDetails.city : selectedBusiness.city || ''}
                          className="edit-business-input"
                          onChange={(e) => {
                            setBusinessDetails(prev => ({ ...prev, city: e.target.value }));
                            handleCityChange(e.target.value);
                          }}
                        />
                      </div>
                      <div className="field-group">
                        <label>State</label>
                        <input
                          type="text"
                          value={businessDetails.state !== undefined ? businessDetails.state : selectedBusiness.state || ''}
                          className="edit-business-input"
                          onChange={(e) => {
                            setBusinessDetails(prev => ({ ...prev, state: e.target.value }));
                            handleStateChange(e.target.value);
                          }}
                        />
                      </div>
                      <div className="field-group">
                        <label>Pincode</label>
                        <input
                          type="text"
                          value={businessDetails.pincode !== undefined ? businessDetails.pincode : selectedBusiness.pincode || ''}
                          className="edit-business-input"
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, pincode: e.target.value }))}
                        />
                      </div>
                      <div className="field-group full-width">
                        <label>Address</label>
                        <input
                          type="text"
                          value={businessDetails.address || autoFilledAddress}
                          className="edit-business-input"
                          placeholder="Address will be auto-filled based on city and state"
                          readOnly
                        />
                      </div>
                      <div className="field-group">
                        <label>Latitude</label>
                        <input
                          type="number"
                          step="any"
                          value={businessDetails.latitude || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, latitude: parseFloat(e.target.value) }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Longitude</label>
                        <input
                          type="number"
                          step="any"
                          value={businessDetails.longitude || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, longitude: parseFloat(e.target.value) }))}
                          className="edit-business-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payment Configuration */}
                  <div className="edit-business-details-section">
                    <h4 style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '8px' : '12px' }}>Payment Configuration</h4>
                    <div className="edit-business-detail-item basic-info">
                      <div className="field-group">
                        <label>Razorpay Key ID</label>
                        <input
                          type="text"
                          value={businessDetails.razor_pay_key_id !== undefined ? businessDetails.razor_pay_key_id : selectedBusiness.razor_pay_key_id || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, razor_pay_key_id: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Razorpay Key Code</label>
                        <input
                          type="text"
                          value={businessDetails.razor_pay_key_code !== undefined ? businessDetails.razor_pay_key_code : selectedBusiness.razor_pay_key_code || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, razor_pay_key_code: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Razorpay Webhook Secret</label>
                        <input
                          type="text"
                          value={businessDetails.razor_webhook_secret !== undefined ? businessDetails.razor_webhook_secret : selectedBusiness.razor_webhook_secret || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, razor_webhook_secret: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group">
                        <label>Payment Gateway</label>
                        <input
                          type="text"
                          value={businessDetails.payment_gateway !== undefined ? businessDetails.payment_gateway : selectedBusiness.payment_gateway || ''}
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, payment_gateway: e.target.value }))}
                          className="edit-business-input"
                        />
                      </div>
                      <div className="field-group full-width">
                        <label>Gateway Credentials</label>
                        <textarea
                          value={
                            businessDetails.gateway_credentials !== undefined
                              ? (typeof businessDetails.gateway_credentials === 'object' && businessDetails.gateway_credentials !== null ? JSON.stringify(businessDetails.gateway_credentials) : businessDetails.gateway_credentials)
                              : (typeof selectedBusiness.gateway_credentials === 'object' && selectedBusiness.gateway_credentials !== null ? JSON.stringify(selectedBusiness.gateway_credentials) : selectedBusiness.gateway_credentials || '')
                          }
                          onChange={(e) => setBusinessDetails(prev => ({ ...prev, gateway_credentials: e.target.value }))}
                          className="edit-business-input"
                          rows="4"
                          placeholder='{"access_code": "abc", "merchant_id": "T_08887", "payment_url": "https://qa.phicommerce.com/pg/api/v2/initiateSale", "aggregator_secret": "36e14e446bd44891b29379d27dad93c1"}'
                        />
                      </div>
                    </div>
                  </div>

                  {/* Business Hours */}
                  <div className="edit-business-details-section">
                    <h4>Business Hours</h4>
                    <div className="business-timings-container">
                      {(() => {
                        let parsedBusinessHours = null;
                        if (businessDetails.business_hours) {
                          try {
                            // Parse JSON string if it's a string, otherwise use as-is
                            parsedBusinessHours = typeof businessDetails.business_hours === 'string'
                              ? JSON.parse(businessDetails.business_hours)
                              : businessDetails.business_hours;
                          } catch (error) {
                            console.error('Error parsing business hours:', error);
                          }
                        }

                        const standardDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                        const currentHours = (editableBusinessHours && Object.keys(editableBusinessHours).length > 0)
                          ? editableBusinessHours
                          : (parsedBusinessHours && typeof parsedBusinessHours === 'object' && Object.keys(parsedBusinessHours).length > 0)
                            ? parsedBusinessHours
                            : {};

                        return (
                          <div className="time-slots-rows">
                            {standardDays.map((day) => {
                              // If currentHours has the day, use it. If it's not an array (e.g. single object), wrap it. 
                              // If missing, default to one empty slot so the user can easily start typing.
                              let slots = currentHours[day];
                              if (!slots || (Array.isArray(slots) && slots.length === 0)) {
                                slots = [{ open: '', close: '' }];
                              } else if (!Array.isArray(slots)) {
                                slots = [slots];
                              }

                              return (
                                <div key={day} className="day-row">
                                  <div className="day-label">
                                    <span className="day-name">{day.substring(0, 3).toUpperCase()}</span>
                                  </div>
                                  <div className="time-slots-horizontal">
                                    {slots.map((slot, index) => (
                                      <div key={index} className="time-slot-editable">
                                        <input
                                          type="time"
                                          value={editableBusinessHours[day]?.[index]?.open || slot.open || ''}
                                          onChange={(e) => updateTimeSlot(day, index, 'open', e.target.value)}
                                          className="time-input"
                                          placeholder="Open"
                                        />
                                        <span className="time-separator">-</span>
                                        <input
                                          type="time"
                                          value={editableBusinessHours[day]?.[index]?.close || slot.close || ''}
                                          onChange={(e) => updateTimeSlot(day, index, 'close', e.target.value)}
                                          className="time-input"
                                          placeholder="Close"
                                        />
                                        <button
                                          className="remove-time-slot-btn"
                                          onClick={() => removeTimeSlot(day, index)}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      className="add-time-slot-btn"
                                      onClick={() => addTimeSlot(day)}
                                    >
                                      + Add Slot
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );

                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="edit-business-modal-actions">
              <button
                className="edit-business-btn-cancel"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="edit-business-btn-save"
                onClick={saveBusinessDetails}
              >
                <FaSave />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Business Details Modal */}
      {showDetailsModal && editingBusiness && (
        <div className="modal-overlay">
          <div className="details-modal-modern">
            {/* Fixed Header */}
            <div className="modal-header-fixed">
              <div className="modal-title-section">
                <div className="modal-icon business-icon">
                  <FaStore />
                </div>
                <div className="title-info">
                  <h3>Business Details</h3>
                  <span className="business-name">{editingBusiness.businessName}</span>
                </div>
              </div>
              <button
                className="close-btn-modern"
                onClick={handleCloseDetailsModal}
              >
                <FaTimes />
              </button>
            </div>

            {/* Tabs */}
            <div className="modal-tabs">
              <button
                className={`modal-tab ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Business Details
              </button>
              <button
                className={`modal-tab ${activeTab === 'orders' ? 'active' : ''}`}
                onClick={() => setActiveTab('orders')}
              >
                Revenue Analytics
              </button>
              <button
                className={`modal-tab ${activeTab === 'staff' ? 'active' : ''}`}
                onClick={() => setActiveTab('staff')}
              >
                Staff Management
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="modal-content-scrollable">
              <div className="details-form-modern">

                {/* Business Details Tab */}
                {activeTab === 'details' && (
                  <>
                    {/* Basic Information */}
                    <div className="form-section">
                      <h4>Basic Information</h4>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Business Name</label>
                          <input
                            type="text"
                            value={businessDetails.businessName}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Business ID</label>
                          <input
                            type="text"
                            value={editingBusiness.business_id}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Business Type</label>
                          <input
                            type="text"
                            value={businessDetails.business_type_name}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            value={businessDetails.businessEmail}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Phone Number</label>
                          <input
                            type="tel"
                            value={businessDetails.businessNumber}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Address Information */}
                    <div className="form-section">
                      <h4>Address Information</h4>
                      <div className="form-grid">
                        <div className="form-group full-width">
                          <label>Address</label>
                          <textarea
                            value={businessDetails.address}
                            disabled
                            className="disabled-field"
                            rows="3"
                          />
                        </div>
                        <div className="form-group">
                          <label>City</label>
                          <input
                            type="text"
                            value={businessDetails.city}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>State</label>
                          <input
                            type="text"
                            value={businessDetails.state}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Pincode</label>
                          <input
                            type="text"
                            value={businessDetails.pincode}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Business Hierarchy */}
                    <div className="form-section">
                      <h4>Business Hierarchy</h4>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Business Level</label>
                          <input
                            type="text"
                            value={editingBusiness.level}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Master Business</label>
                          <input
                            type="text"
                            value={editingBusiness.master || 'None'}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Is Master</label>
                          <input
                            type="text"
                            value={editingBusiness.is_master ? 'Yes' : 'No'}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Has Subsidiaries</label>
                          <input
                            type="text"
                            value={editingBusiness.has_subs ? 'Yes' : 'No'}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Business Classification */}
                    <div className="form-section">
                      <h4>Business Classification</h4>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Business Type Code</label>
                          <input
                            type="text"
                            value={editingBusiness.businessType}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Business Category</label>
                          <input
                            type="text"
                            value={editingBusiness.businessCategory}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Business Status */}
                    <div className="form-section">
                      <h4>Business Status</h4>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>Status</label>
                          <input
                            type="text"
                            value={businessDetails.status}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>Payment Status</label>
                          <input
                            type="text"
                            value={businessDetails.payment_status}
                            disabled
                            className="disabled-field"
                          />
                        </div>
                      </div>
                    </div>

                  </>
                )}

                {/* Revenue Analytics Tab */}
                {activeTab === 'orders' && (
                  <>
                    {/* Revenue Analytics */}
                    <div className="form-section">
                      <h4>Revenue Analytics</h4>
                      <div className="revenue-breakdown">
                        <div className="revenue-summary">
                          <div className="summary-item">
                            <span className="label">Total Orders</span>
                            <span className="value">{editingBusiness.total_orders}</span>
                          </div>
                          <div className="summary-item">
                            <span className="label">Total Revenue</span>
                            <span className="value">{formatCurrency(editingBusiness.total_revenue)}</span>
                          </div>
                        </div>

                        {editingBusiness.revenue_by_status && Object.keys(editingBusiness.revenue_by_status).length > 0 && (
                          <div className="revenue-by-status">
                            <h5>Revenue by Order Status</h5>
                            <div className="revenue-status-grid">
                              {Object.entries(editingBusiness.revenue_by_status).map(([status, data]) => (
                                <div key={status} className="revenue-status-item">
                                  <div className="status-header">
                                    <span className="status-name">{status}</span>
                                    <span className="order-count">{data.order_count} orders</span>
                                  </div>
                                  <div className="revenue-details">
                                    <div className="revenue-item">
                                      <span className="revenue-label">Actual Revenue:</span>
                                      <span className="revenue-value">{formatCurrency(data.revenue)}</span>
                                    </div>
                                    <div className="revenue-item">
                                      <span className="revenue-label">Total Amount:</span>
                                      <span className="revenue-value">{formatCurrency(data.total_amount)}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Staff Management Tab */}
                {activeTab === 'staff' && (
                  <BusinessStaffManagement businessId={editingBusiness?.business_id} />
                )}

              </div>
            </div>

            {/* Fixed Footer */}
            <div className="modal-footer-fixed">
              <button
                className="btn-cancel-modern"
                onClick={handleCloseDetailsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessManagement;
